'use client';

import { motion } from 'framer-motion';
import Button from '@/components/ui/Button';
import { useMissionData } from '@/lib/hooks/useMission';
import { mapMissionFromApi } from '@/lib/mappers/missionMapper';
import { OurMissionLoadingSkeleton } from '@/components/ui/OurMissionLoadingSkeleton';

const OurMissionSectionClient = ({ dict }: { dict: any }) => {
  const { data: apiData, loading, error } = useMissionData();

  const model = apiData ? mapMissionFromApi(apiData) : null;

  if (loading) {
    return <OurMissionLoadingSkeleton />;
  }

  if (error) {
    console.error('Mission data loading failed:', error);
    return null;
  }

  if (!model) {
    return null;
  }

  return (
    <section 
      className="py-20"
      style={{ backgroundColor: model.layout.backgroundColor }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center rounded-3xl p-8"
          style={{
            border: `2px dashed ${model.layout.borderColor}`,
            backgroundColor: model.layout.backgroundColor
          }}
        >
          {/* Left Side - Image */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <motion.img 
                src={model.image.src}
                alt={model.image.alt}
                className="w-full h-full object-cover aspect-[4/3]"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              />
            </div>
          </motion.div>

          {/* Right Side - Content */}
          <motion.div 
            className="text-white"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <p 
              className="text-sm font-semibold uppercase tracking-wide mb-2"
              style={{ 
                fontSize: model.typography.badge.fontSize,
                fontWeight: model.typography.badge.fontWeight,
                textTransform: model.typography.badge.textTransform as any,
                letterSpacing: model.typography.badge.letterSpacing,
                color: model.typography.badge.color
              }}
            >
              {model.badge}
            </p>
            
            <h2
              className="mb-6"
              style={{ 
                fontFamily: model.typography.title.fontFamily,
                fontSize: model.typography.title.fontSize,
                fontWeight: model.typography.title.fontWeight,
                lineHeight: model.typography.title.lineHeight,
                color: model.typography.title.color
              }}
            >
              {model.title}
            </h2>

            <p
              className="mb-8"
              style={{ 
                fontFamily: model.typography.subtitle.fontFamily,
                fontSize: model.typography.subtitle.fontSize,
                fontWeight: model.typography.subtitle.fontWeight,
                lineHeight: model.typography.subtitle.lineHeight,
                color: model.typography.subtitle.color
              }}
            >
              {model.subtitle}
            </p>
            
            <Button 
              variant={model.cta.variant as any}
              size={model.cta.size as any}
              className="rounded-lg"
              style={{
                backgroundColor: model.cta.style.backgroundColor,
                color: model.cta.style.color,
                borderRadius: model.cta.style.borderRadius
              }}
            >
              {model.cta.text}
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default OurMissionSectionClient;
